<template>
  <v-container class="helmi-search-watch" fluid>
    <helmi-search-watch @loading="handleLoading"/>

    <v-row no-gutters class="mt-0">
      <v-col>
        <v-card class="helmi-card">
          <div class="helmi-card-title pt-6 pl-6">
            Aktiiviset hakuvahdit
          </div>
          <helmi-search-watch-active-filters
            v-if="tablet"
            :filters="filters"
            class="mb-1 mx-1"
            color="primary"
            outlined
            resetable
            small
          />

          <v-data-iterator
            v-if="mobile"
            :items="watches"
          >
            <template #no-data>
              <v-list-item>
                <span class="caption grey--text">Ei tuloksia</span>
              </v-list-item>
            </template>
            <template #default="props">
              <v-list dense>
                <v-list-item-group
                  v-model="selected"
                  active-class="secondary--text"
                >
                  <template v-for="(item, index) in props.items">
                    <v-list-item
                      :key="item.title"
                      @click="onRowClick(item, $event)"
                    >
                      <template #default>
                        <v-list-item-content>
                          <v-list-item-title v-if="item.person" v-text="item.person.name"/>
                          <v-list-item-subtitle>
                            {{ item.created | date }} - {{ item.ends | date }}
                          </v-list-item-subtitle>
                          <v-list-item-subtitle>
                            {{ item.target_type === 'sales' ? 'Myyntikohde' : 'Vuokrakohde' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>

                        <v-list-item-action>
                          <span class="text-caption">
                            Osumia
                          </span>
                          <v-list-item-action-text>
                            <v-chip
                              :color="item.hits ? 'secondary' : 'grey lighten-2'"
                              dark
                              x-small
                            >
                              {{ item.hits }}
                            </v-chip>
                          </v-list-item-action-text>
                        </v-list-item-action>
                      </template>
                    </v-list-item>

                    <v-divider
                      v-if="index + 1 < props.items.length"
                      :key="index"
                    />
                  </template>
                </v-list-item-group>
              </v-list>
            </template>
          </v-data-iterator>

          <v-data-table
            v-else
            :headers="active.headers"
            :items="watches"
            :loading="loading"
            class="elevation-0"
            item-key="name"
            @click:row="onRowClick"
          >
            <template #item.created="{ item }">
              {{ item.created | date }}
            </template>

            <template #item.target_type="{ item }">
              {{ item.target_type === 'sales' ? 'Myyntikohde' : 'Vuokrakohde' }}
            </template>

            <template #item.ends="{ item }">
              {{ item.ends | date }}
            </template>

            <template #item.hits="{ item }">
              <v-chip
                :color="item.hits ? 'secondary' : 'grey lighten-2'"
                dark
                small
              >
                {{ item.hits }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <helmi-search-watch-dialog
      :data="dialogData"
      :headers="hitTable.headers"
      :show="dialog"
      :processing="processing"
      @close="onDialogClose"
    />
  </v-container>
</template>

<script>
/* eslint-disable no-param-reassign */
import { mapActions, mapGetters } from 'vuex';
import { EventBus, isAnObject } from '@/utils';

export default {
  name: 'HelmiSearchWatchList',

  components: {
    'helmi-search-watch': () => import('@/components/searchWatch/SearchWatch'),
    'helmi-search-watch-dialog': () => import('@/components/searchWatch/SearchWatchDialog'),
    'helmi-search-watch-active-filters': () => import('@/components/searchWatch/searchWatchActiveFilters'),
  },

  data: () => ({
    chips: [],
    loading: false,
    filteredData: [],
    filters: {},
    dialog: false,
    dialogData: {},
    filter: false,
    hitTable: {
      headers: [
        { text: 'Osoite', value: 'address' },
        { text: 'Tyyppi', value: 'type' },
        { text: 'Avaa', value: 'id' },
        { text: 'Hinta', value: 'price' },
        { text: 'Huoneita', value: 'roomSize' },
        { text: 'Lähetetty', value: 'sent', align: 'end' },
      ],
    },
    active: {
      expanded: [],
      headers: [
        { text: 'Asiakas', value: 'person.name' },
        { text: 'Tyyppi', value: 'target_type' },
        { text: 'Osumia', value: 'hits' },
        { text: 'Luotu', value: 'created' },
        { text: 'Voimassa asti', value: 'ends' },
      ],
      data: [],
    },
    processing: false,
    selected: null,
  }),

  computed: {
    ...mapGetters({
      searchWatches: 'searchWatch/list',
    }),

    id() {
      return this.$route.params.companyId;
    },

    mobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    tablet() {
      return this.$vuetify.breakpoint.mdAndDown;
    },

    watches() {
      return this.searchWatches
        .filter(watch => parseInt(watch.active, 10) > 0);
    },
  },

  mounted() {
    // console.log('🍄', this.$vuetify.theme.themes.light.accent);
    EventBus.$on('REST_FILTERS', this.resetFilters);
    EventBus.$on('SEARCH_WATCH_FILTERS_CHANGED', this.onFiltersChanged);
  },

  methods: {
    ...mapActions({
      setCurrentProperty: 'setCurrentProperty',
      getWatches: 'searchWatch/get',
      getPerson: 'getPerson',
    }),

    getInitials(name) {
      return name
        .split(' ')
        .slice(0, 2)
        .map(p => p.slice(0, 1))
        .join('')
        .toUpperCase();
    },

    handleLoading(loading) {
      this.loading = loading;
    },

    onDialogClose() {
      this.dialog = false;
    },

    onFiltersChanged(items) {
      const filters = Object.entries(items).reduce((obj, [key, value]) => {
        if (Array.isArray(value)) {
          if (key === 'cities') {
            obj.cities = value.filter(v => !v.match(/^\d+$/));
            obj.zips = value.filter(v => v.match(/^\d+$/));
          } else {
            obj[key] = value.length ? value.join(',') : null;
          }
        } else if (isAnObject(value)) {
          Object.entries(value)
            .forEach(([k, v]) => {
              obj[k] = v;
            });
        } else {
          obj[key] = value;
        }
        return obj;
      }, {});

      this.$set(this, 'filters', filters);
    },

    /**
     * Toggle row expansion on click
     */
    async onRowClick(item, value) {
      this.dialog = true;
      if (!value || item.customer) return;
      this.processing = true;

      setTimeout(() => {
        this.processing = false;
      }, 500);

      this.dialogData = item;
    },

    resetFilters() {
      this.getData();
    },
  },
};
</script>

<style lang="scss">
.helmi-search-watch {
  .v-toolbar,
  .v-toolbar__extension {
    height: auto !important;
  }
}
</style>
